import Transaction from './Transaction';
import { useTheme } from '../../contexts/Theme.context';
import { useCallback, useEffect, useState } from 'react';
import useWallets from '../../api/wallets';
import { useParams } from 'react-router';

/**
 * Renders a list of transactions.
 * This component can EITHER display transactions passed via props
 * OR fetch its own transactions if 'canFetch' is true.
 *
 * @param {object[]} transactions - The transactions to display (used if canFetch is false).
 * @param {function} clickTransaction - Callback function when a transaction is clicked.
 * @param {boolean} canFetch - If true, the component will fetch its own data.
 */
export default function TransactionList({ transactions, clickTransaction, canFetch = false }) {

  const { compCode, walletCode } = useParams();
  const { getTransactionsOfWallet } = useWallets();

  // 'trans' state holds the list of transactions to be rendered
  const [trans, setTrans] = useState(transactions);

  // --- EFFECT 1: Handle Fetching Data ---
  // This effect runs when 'canFetch', 'compCode', or 'walletCode' changes.
  useEffect(() => {
    
    const fetchTransactions = async () => {
      try {
        const result = await getTransactionsOfWallet({
          compId: compCode,
          walletId: walletCode
        });
        console.log(result);
        // Set state to the fetched transactions (or an empty array)
        setTrans(result?.transactions || []); 
      } catch (error) {
        console.error("Failed to fetch transactions:", error);
        setTrans([]); // Clear state on error
      }
    };

    // Only fetch if canFetch is true and we have the necessary params
    if (canFetch && compCode && walletCode) {
      // Optional: Set loading state here, e.g., setTrans(undefined)
      fetchTransactions();
    }

  }, [canFetch, compCode, walletCode, getTransactionsOfWallet]); // Dependencies that trigger a refetch

  // --- EFFECT 2: Handle Prop Updates ---
  // This effect synchronizes the state with the 'transactions' prop
  // *only if* the component is NOT responsible for fetching.
  useEffect(() => {
    if (!canFetch) {
      setTrans(transactions);
    }
  }, [transactions, canFetch]); // Update state if props change (and we aren't fetching)


  const { oppositeTheme } = useTheme();

  // Add 'clickTransaction' to dependency array for correctness
  const handleClick = useCallback((id) => {
    clickTransaction(id);
  }, [clickTransaction]);

  // Add safety checks to ensure 'trans' is an array before mapping or accessing
  const hasTransactions = Array.isArray(trans) && trans.length > 0;

  return (
    <>
      <table className="table table-responsive table-sm table-striped">
        <thead>
          <tr className={'m-4'}>
            <th className={`text-${oppositeTheme}`}>Datum</th>
            <th className={`text-${oppositeTheme} d-none d-lg-table-cell`}>Tijd</th>
            {/* Check if 'trans' is a non-empty array before accessing [0] */}
            {hasTransactions && trans[0]?.firstname ? (
              <th className={`text-${oppositeTheme}`}>Gebruiker</th>
            ) : null}
            <th className={`text-${oppositeTheme}`}>Bedrag</th>
          </tr>
        </thead>
        <tbody>
          {/* Ensure 'trans' is an array before mapping to prevent errors */}
          {hasTransactions && trans.map(t => (
            <Transaction
              clickTransaction={handleClick}
              key={t.id}
              id={t.id}
              firstname={t.firstname}
              lastname={t.lastname}
              amount={t.amount}
              timestamp={t.timestamp}
              orderlines={t.orderlines}
            />
          ))}
        </tbody>
      </table>
    </>
  );
}